import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import {supabase} from './lib/supabase';
import './App.css';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AdminLogin from './admin/AdminLogin';
import AdminPanel from './admin/AdminPanel';
import PDFFormularAdmin from './admin/PDFFormularAdmin';
import IPCaptureTester from './admin/IPCaptureTester';
import {fetchMenuOptions,testMenuDatabase} from './utils/menuService';
import {getOrderOptions} from './utils/configService';
import {createOrder} from './utils/orderService';

// Import icons from react-icons/fi
const {
  FiCheck,FiShoppingCart,FiMapPin,FiClock,FiPhone,FiMail,FiUser,
  FiPlus,FiMinus,FiTrash2,FiLoader,FiAlertCircle,FiSettings,
  FiFire,FiGlobe,FiFileText
}=FiIcons;

function App() {
  // State for admin section
  const [showAdmin,setShowAdmin]=useState(false);
  const [adminLoggedIn,setAdminLoggedIn]=useState(false);
  const [showPDFFormular,setShowPDFFormular]=useState(false);
  const [showIPTester,setShowIPTester]=useState(false);

  // State for burger/pizza order
  const [productType,setProductType]=useState('burger');
  const [burgerOptions,setBurgerOptions]=useState({
    pattyOptions: [],
    bunOptions: [],
    toppings: [],
    sauces: [],
    sides: [],
    spicyLevels: []
  });
  const [pizzaOptions,setPizzaOptions]=useState({
    baseOptions: [],
    toppings: [],
    sauces: [],
    sides: [],
    spicyLevels: []
  });
  const [orderConfig,setOrderConfig]=useState({
    burger: {pickup_enabled: true,delivery_enabled: true},
    pizza: {pickup_enabled: true,delivery_enabled: true}
  });

  // Order form state
  const [orderForm,setOrderForm]=useState({
    name: '',
    phone: '',
    email: '',
    orderType: 'pickup',
    pickupTime: '',
    deliveryAddress: '',
    pattys: [],
    bases: [],
    size: 'medium',
    bun: 'classic-sesame',
    toppings: {},
    sauces: {},
    spicyLevels: {},
    sides: {},
    sideSauces: {},
    specialRequests: '',
    totalPrice: 0
  });

  const [loading,setLoading]=useState(true);
  const [submitting,setSubmitting]=useState(false);
  const [orderSuccess,setOrderSuccess]=useState(null);
  const [orderError,setOrderError]=useState(null);
  const [databaseTestResult,setDatabaseTestResult]=useState(null);

  // Load menu options on component mount
  useEffect(()=> {
    loadMenuOptions();
    loadOrderConfig();
  },[]);

  // Load menu options from the database
  const loadMenuOptions=async ()=> {
    setLoading(true);
    console.log('🔄 Lade Menüoptionen für beide Produkttypen...');
    try {
      // Test database connection first
      console.log('🧪 Teste Menü-Datenbank...');
      const testResult=await testMenuDatabase();
      setDatabaseTestResult(testResult);

      // Load burger options
      const burgerData=await fetchMenuOptions('burger');
      setBurgerOptions(burgerData);
      console.log('✅ Burger-Optionen geladen!',burgerData.pattyOptions.length,'Patties');

      // Load pizza options
      const pizzaData=await fetchMenuOptions('pizza');
      setPizzaOptions(pizzaData);
      console.log('✅ Pizza-Optionen geladen!',pizzaData.baseOptions.length,'Basen');

      // Set default bun if available
      if (burgerData.bunOptions && burgerData.bunOptions.length > 0) {
        setOrderForm(prev=> ({...prev,bun: burgerData.bunOptions[0].id}));
      }

    } catch (error) {
      console.error('❌ Fehler beim Laden der Menüoptionen:',error);
    } finally {
      setLoading(false);
    }
  };

  // Load order configuration (pickup/delivery options)
  const loadOrderConfig=async ()=> {
    try {
      const config=await getOrderOptions();
      if (config) {
        setOrderConfig(config);
        // Update orderType if current type is disabled
        setOrderForm(prev=> {
          // If current order type is pickup but pickup is disabled,switch to delivery
          if (prev.orderType==='pickup' && !config[productType].pickup_enabled) {
            return {...prev,orderType: 'delivery'};
          }
          // If current order type is delivery but delivery is disabled,switch to pickup
          else if (prev.orderType==='delivery' && !config[productType].delivery_enabled) {
            return {...prev,orderType: 'pickup'};
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der Bestelloptionen:',error);
    }
  };

  // Get current menu options based on product type
  const getCurrentOptions=()=> {
    return productType==='burger' ? burgerOptions : pizzaOptions;
  };

  // Handle product type change
  const handleProductTypeChange=(type)=> {
    setProductType(type);
    // Reset order form when changing product type
    setOrderForm(prev=> {
      // Determine order type based on config for the new product type
      let newOrderType=prev.orderType;
      if (prev.orderType==='pickup' && !orderConfig[type].pickup_enabled) {
        newOrderType='delivery';
      } else if (prev.orderType==='delivery' && !orderConfig[type].delivery_enabled) {
        newOrderType='pickup';
      }
      return {
        ...prev,
        pattys: [],
        bases: [],
        size: 'medium',
        bun: burgerOptions.bunOptions?.length > 0 ? burgerOptions.bunOptions[0].id : 'classic-sesame',
        toppings: {},
        sauces: {},
        spicyLevels: {},
        sides: {},
        sideSauces: {},
        orderType: newOrderType,
        totalPrice: 0
      };
    });
  };

  // Handle order form changes
  const handleOrderFormChange=(field,value)=> {
    setOrderForm(prev=> ({...prev,[field]: value}));
  };

  // Handle patty selection
  const handlePattySelection=(pattyId,increment)=> {
    setOrderForm(prev=> {
      const existingPatty=prev.pattys.find(p=> p.id===pattyId);
      let updatedPattys;
      if (existingPatty) {
        // Update existing patty count
        const newCount=existingPatty.count + (increment ? 1 : -1);
        if (newCount <=0) {
          // Remove patty if count is 0 or less
          updatedPattys=prev.pattys.filter(p=> p.id !==pattyId);
        } else {
          // Update count
          updatedPattys=prev.pattys.map(p=>
            p.id===pattyId ? {...p,count: newCount} : p
          );
        }
      } else if (increment) {
        // Add new patty with count 1
        updatedPattys=[...prev.pattys,{id: pattyId,count: 1}];
      } else {
        updatedPattys=prev.pattys;
      }
      return {...prev,pattys: updatedPattys};
    });
  };

  // Handle base selection
  const handleBaseSelection=(baseId,increment)=> {
    setOrderForm(prev=> {
      const existingBase=prev.bases.find(b=> b.id===baseId);
      let updatedBases;
      if (existingBase) {
        // Update existing base count
        const newCount=existingBase.count + (increment ? 1 : -1);
        if (newCount <=0) {
          // Remove base if count is 0 or less
          updatedBases=prev.bases.filter(b=> b.id !==baseId);
        } else {
          // Update count
          updatedBases=prev.bases.map(b=>
            b.id===baseId ? {...b,count: newCount} : b
          );
        }
      } else if (increment) {
        // Add new base with count 1
        updatedBases=[...prev.bases,{id: baseId,count: 1}];
      } else {
        updatedBases=prev.bases;
      }
      return {...prev,bases: updatedBases};
    });
  };

  // Handle topping selection
  const handleToppingSelection=(toppingId,increment)=> {
    setOrderForm(prev=> {
      const currentTopping=prev.toppings[toppingId] || {count: 0};
      const newCount=currentTopping.count + (increment ? 1 : -1);
      const updatedToppings={
        ...prev.toppings,
        [toppingId]: {count: Math.max(0,newCount)}
      };
      // Remove topping if count is 0
      if (updatedToppings[toppingId].count===0) {
        delete updatedToppings[toppingId];
      }
      return {...prev,toppings: updatedToppings};
    });
  };

  // Handle sauce selection
  const handleSauceSelection=(sauceId,increment)=> {
    setOrderForm(prev=> {
      const currentSauce=prev.sauces[sauceId] || {count: 0};
      const newCount=currentSauce.count + (increment ? 1 : -1);
      const updatedSauces={
        ...prev.sauces,
        [sauceId]: {count: Math.max(0,newCount)}
      };
      // Remove sauce if count is 0
      if (updatedSauces[sauceId].count===0) {
        delete updatedSauces[sauceId];
      }
      return {...prev,sauces: updatedSauces};
    });
  };

  // Handle spicy level selection
  const handleSpicyLevelSelection=(sauceId,level)=> {
    setOrderForm(prev=> {
      return {...prev,spicyLevels: {...prev.spicyLevels,[sauceId]: level}};
    });
  };

  // Handle side selection
  const handleSideSelection=(sideId,increment)=> {
    setOrderForm(prev=> {
      const currentSide=prev.sides[sideId] || {count: 0};
      const newCount=currentSide.count + (increment ? 1 : -1);
      const updatedSides={
        ...prev.sides,
        [sideId]: {count: Math.max(0,newCount)}
      };
      // Remove side if count is 0
      if (updatedSides[sideId].count===0) {
        delete updatedSides[sideId];
      }
      return {...prev,sides: updatedSides};
    });
  };

  // Handle side sauce selection
  const handleSideSauceSelection=(sauceId,increment)=> {
    setOrderForm(prev=> {
      const currentSauce=prev.sideSauces[sauceId] || {count: 0};
      const newCount=currentSauce.count + (increment ? 1 : -1);
      const updatedSideSauces={
        ...prev.sideSauces,
        [sauceId]: {count: Math.max(0,newCount)}
      };
      // Remove sauce if count is 0
      if (updatedSideSauces[sauceId].count===0) {
        delete updatedSideSauces[sauceId];
      }
      return {...prev,sideSauces: updatedSideSauces};
    });
  };

  // Calculate total price
  useEffect(()=> {
    const calculateTotal=()=> {
      let total=0;
      // Add patty prices
      if (productType==='burger') {
        orderForm.pattys.forEach(patty=> {
          const pattyOption=burgerOptions.pattyOptions.find(p=> p.id===patty.id);
          if (pattyOption) {
            total +=pattyOption.price * patty.count;
          }
        });
      } else {
        // Add base prices
        orderForm.bases.forEach(base=> {
          const baseOption=pizzaOptions.baseOptions.find(b=> b.id===base.id);
          if (baseOption) {
            total +=baseOption.price * base.count;
          }
        });
        // Add size price
        if (orderForm.size==='medium') {
          total +=3;
        } else if (orderForm.size==='large') {
          total +=6;
        }
      }

      // Add topping prices
      const toppingOptions=productType==='burger' ? burgerOptions.toppings : pizzaOptions.toppings;
      Object.entries(orderForm.toppings).forEach(([id,data])=> {
        const topping=toppingOptions.find(t=> t.id===id);
        if (topping) {
          total +=topping.price * data.count;
        }
      });

      // Add sauce prices
      const sauceOptions=productType==='burger' ? burgerOptions.sauces : pizzaOptions.sauces;
      Object.entries(orderForm.sauces).forEach(([id,data])=> {
        const sauce=sauceOptions.find(s=> s.id===id);
        if (sauce) {
          total +=sauce.price * data.count;
        }
      });

      // Add side prices
      const sideOptions=productType==='burger' ? burgerOptions.sides : pizzaOptions.sides;
      Object.entries(orderForm.sides).forEach(([id,data])=> {
        const side=sideOptions.find(s=> s.id===id);
        if (side) {
          total +=side.price * data.count;
        }
      });

      // Add side sauce prices
      Object.entries(orderForm.sideSauces).forEach(([id,data])=> {
        const sauce=sauceOptions.find(s=> s.id===id);
        if (sauce) {
          total +=sauce.price * data.count;
        }
      });

      return total;
    };
    const total=calculateTotal();
    setOrderForm(prev=> ({...prev,totalPrice: total}));
  },[
    productType,
    orderForm.pattys,
    orderForm.bases,
    orderForm.size,
    orderForm.toppings,
    orderForm.sauces,
    orderForm.sides,
    orderForm.sideSauces,
    burgerOptions.pattyOptions,
    burgerOptions.toppings,
    burgerOptions.sauces,
    burgerOptions.sides,
    pizzaOptions.baseOptions,
    pizzaOptions.toppings,
    pizzaOptions.sauces,
    pizzaOptions.sides
  ]);

  // Submit order
  const handleSubmitOrder=async (e)=> {
    e.preventDefault();
    setSubmitting(true);
    setOrderError(null);
    try {
      // Validate form
      if (!orderForm.name || !orderForm.phone || !orderForm.email) {
        throw new Error('Bitte füllen Sie alle Pflichtfelder aus (Name,Telefon,E-Mail).');
      }
      if (productType==='burger' && (!orderForm.pattys || orderForm.pattys.length===0)) {
        throw new Error('Bitte wählen Sie mindestens ein Patty aus.');
      }
      if (productType==='pizza' && (!orderForm.bases || orderForm.bases.length===0)) {
        throw new Error('Bitte wählen Sie mindestens eine Basis aus.');
      }
      // Only validate delivery address if delivery is enabled and selected
      if (orderForm.orderType==='delivery' && orderConfig[productType].delivery_enabled && !orderForm.deliveryAddress) {
        throw new Error('Bitte geben Sie eine Lieferadresse ein.');
      }
      // Only validate pickup time if pickup is enabled and selected
      if (orderForm.orderType==='pickup' && orderConfig[productType].pickup_enabled && !orderForm.pickupTime) {
        throw new Error('Bitte wählen Sie eine Abholzeit.');
      }

      // Prepare order data
      const orderData={
        ...orderForm,
        productType,
        // Include menu options for email formatting
        pattyOptions: burgerOptions.pattyOptions,
        bunOptions: burgerOptions.bunOptions,
        baseOptions: pizzaOptions.baseOptions,
        toppingOptions: productType==='burger' ? burgerOptions.toppings : pizzaOptions.toppings,
        sauceOptions: productType==='burger' ? burgerOptions.sauces : pizzaOptions.sauces,
        sideOptions: productType==='burger' ? burgerOptions.sides : pizzaOptions.sides,
        spicyLevelOptions: productType==='burger' ? burgerOptions.spicyLevels : pizzaOptions.spicyLevels
      };

      // Submit order to backend
      const result=await createOrder(orderData);

      // Reset form on success
      setOrderSuccess(result);
      setOrderForm({
        name: '',
        phone: '',
        email: '',
        orderType: 'pickup',
        pickupTime: '',
        deliveryAddress: '',
        pattys: [],
        bases: [],
        size: 'medium',
        bun: burgerOptions.bunOptions?.length > 0 ? burgerOptions.bunOptions[0].id : '',
        toppings: {},
        sauces: {},
        spicyLevels: {},
        sides: {},
        sideSauces: {},
        specialRequests: '',
        totalPrice: 0
      });
    } catch (error) {
      console.error('❌ Fehler bei der Bestellung:',error);
      setOrderError(error.message || 'Ein unbekannter Fehler ist aufgetreten.');
    } finally {
      setSubmitting(false);
    }
  };


  // Admin login handler
  const handleAdminLogin=()=> {
    setAdminLoggedIn(true);
  };

  // Admin logout handler
  const handleAdminLogout=()=> {
    setAdminLoggedIn(false);
    localStorage.removeItem('adminAuth');
    setShowAdmin(false);
  };

  // If PDF Formular or IP Tester is shown
  if (showPDFFormular || showIPTester) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b shadow-sm p-4">
          <div className="container mx-auto flex justify-between items-center">
            <h1 className="text-xl font-bold text-orange-600 flex items-center">
              <SafeIcon icon={FiSettings} className="mr-2" />
              {showPDFFormular ? '🍔🍕 Wunsch-Burger & Pizza PDF Formular' : '🔍 IP-Adresse Tester'}
            </h1>
            <button
              onClick={()=> {
                setShowPDFFormular(false);
                setShowIPTester(false);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center"
            >
              <SafeIcon icon={FiSettings} className="mr-2" /> Zurück zur App
            </button>
          </div>
        </div>
        {showPDFFormular && <PDFFormularAdmin />}
        {showIPTester && <IPCaptureTester />}
      </div>
    );
  }

  // Admin panel view
  if (showAdmin) {
    return adminLoggedIn ? (
      <AdminPanel onLogout={handleAdminLogout} />
    ) : (
      <AdminLogin onLoginSuccess={handleAdminLogin} />
    );
  }

  // Main app
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-500 to-red-600 text-white">
      {/* Header */}
      <header className="relative py-6 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex justify-center items-center">
          <div className="text-center">
            <motion.h1
              className="text-4xl md:text-5xl font-bold"
              initial={{y: -50,opacity: 0}}
              animate={{y: 0,opacity: 1}}
              transition={{duration: 0.5}}
            >
              {productType==='burger' ? '🍔 Wunsch-Burger' : '🍕 Wunsch-Pizza'}
            </motion.h1>
            <motion.p
              className="mt-2 text-xl"
              initial={{y: -20,opacity: 0}}
              animate={{y: 0,opacity: 1}}
              transition={{duration: 0.5,delay: 0.1}}
            >
              {productType==='burger' ? 'Gestalte deinen Traumburger!' : 'Kreiere deine perfekte Pizza!'}
            </motion.p>
          </div>
        </div>
      </header>

      {/* Product type selector */}
      <div className="max-w-6xl mx-auto px-4 mb-8">
        <div className="flex justify-center space-x-4">
          <button
            onClick={()=> handleProductTypeChange('burger')}
            className={`px-6 py-3 rounded-full font-bold ${productType==='burger' ? 'bg-white text-orange-600 shadow-lg' : 'bg-orange-600/30 text-white hover:bg-orange-600/50'}`}
          >
            🍔 Burger
          </button>
          <button
            onClick={()=> handleProductTypeChange('pizza')}
            className={`px-6 py-3 rounded-full font-bold ${productType==='pizza' ? 'bg-white text-orange-600 shadow-lg' : 'bg-orange-600/30 text-white hover:bg-orange-600/50'}`}
          >
            🍕 Pizza
          </button>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto p-4">
        {loading ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiLoader} className="animate-spin mx-auto text-white text-2xl mb-4" />
            <p>Lade Menüoptionen...</p>
          </div>
        ) : databaseTestResult && (!databaseTestResult.burger.available || !databaseTestResult.pizza.available) ? (
          <div className="bg-red-500/20 border border-red-200 rounded-xl p-6 text-center">
            <SafeIcon icon={FiAlertCircle} className="text-4xl mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Datenbankverbindung fehlgeschlagen</h2>
            <p>Leider konnte keine Verbindung zur Datenbank hergestellt werden.</p>
            <p className="mt-4">Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Order form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-xl overflow-hidden text-gray-800">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                  <h2 className="text-2xl font-bold">
                    {productType==='burger' ? '🍔 Burger zusammenstellen' : '🍕 Pizza zusammenstellen'}
                  </h2>
                  <p className="opacity-80">
                    Wählen Sie Ihre Zutaten und erstellen Sie Ihr perfektes {productType==='burger' ? 'Burger' : 'Pizza'}-Erlebnis
                  </p>
                </div>
                <div className="p-6">
                  {/* Burger patties or Pizza bases */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">1</span>
                      {productType==='burger' ? 'Burger-Patty wählen' : 'Pizza-Basis wählen'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {productType==='burger' ? (
                        burgerOptions.pattyOptions.map((patty)=> {
                          const selected=orderForm.pattys.find(p=> p.id===patty.id);
                          const count=selected ? selected.count : 0;
                          return (
                            <div key={patty.id} className={`border rounded-lg p-3 flex justify-between items-center ${count > 0 ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                              <div>
                                <p className="font-medium">{patty.name}</p>
                                <p className="text-sm text-gray-600">{patty.price.toFixed(2)} CHF</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={()=> handlePattySelection(patty.id,false)}
                                  disabled={count===0}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${count===0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                                >
                                  <SafeIcon icon={FiMinus} />
                                </button>
                                <span className="w-6 text-center font-medium">{count}</span>
                                <button onClick={()=> handlePattySelection(patty.id,true)} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600">
                                  <SafeIcon icon={FiPlus} />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        pizzaOptions.baseOptions.map((base)=> {
                          const selected=orderForm.bases.find(b=> b.id===base.id);
                          const count=selected ? selected.count : 0;
                          return (
                            <div key={base.id} className={`border rounded-lg p-3 flex justify-between items-center ${count > 0 ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                              <div>
                                <p className="font-medium">{base.name}</p>
                                <p className="text-sm text-gray-600">{base.price.toFixed(2)} CHF</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={()=> handleBaseSelection(base.id,false)}
                                  disabled={count===0}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${count===0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                                >
                                  <SafeIcon icon={FiMinus} />
                                </button>
                                <span className="w-6 text-center font-medium">{count}</span>
                                <button onClick={()=> handleBaseSelection(base.id,true)} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600">
                                  <SafeIcon icon={FiPlus} />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Burger bun or Pizza size */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">2</span>
                      {productType==='burger' ? 'Brötchen wählen' : 'Größe wählen'}
                    </h3>
                    {productType==='burger' ? (
                      <div className="flex flex-wrap gap-3">
                        {burgerOptions.bunOptions.map(bun=> (
                          <label key={bun.id} className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer">
                            <input
                              type="radio"
                              name="bun"
                              checked={orderForm.bun===bun.id}
                              onChange={()=> handleOrderFormChange('bun',bun.id)}
                              className="w-4 h-4 text-orange-500"
                            />
                            <span>{bun.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer">
                          <input type="radio" checked={orderForm.size==='small'} onChange={()=> handleOrderFormChange('size','small')} className="w-4 h-4 text-orange-500" />
                          <div>
                            <span>Klein (26cm)</span>
                            <p className="text-sm text-gray-600">Basis</p>
                          </div>
                        </label>
                        <label className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer">
                          <input type="radio" checked={orderForm.size==='medium'} onChange={()=> handleOrderFormChange('size','medium')} className="w-4 h-4 text-orange-500" />
                          <div>
                            <span>Mittel (30cm)</span>
                            <p className="text-sm text-gray-600">+3.00 CHF</p>
                          </div>
                        </label>
                        <label className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer">
                          <input type="radio" checked={orderForm.size==='large'} onChange={()=> handleOrderFormChange('size','large')} className="w-4 h-4 text-orange-500" />
                          <div>
                            <span>Groß (36cm)</span>
                            <p className="text-sm text-gray-600">+6.00 CHF</p>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Toppings */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">3</span>
                      Toppings (optional)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(productType==='burger' ? burgerOptions.toppings : pizzaOptions.toppings).map((topping)=> {
                        const selected=orderForm.toppings[topping.id];
                        const count=selected ? selected.count : 0;
                        return (
                          <div key={topping.id} className={`border rounded-lg p-3 flex justify-between items-center ${count > 0 ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                            <div>
                              <p className="font-medium">{topping.name}</p>
                              <p className="text-sm text-gray-600">{topping.price.toFixed(2)} CHF</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={()=> handleToppingSelection(topping.id,false)}
                                disabled={count===0}
                                className={`w-8 h-8 rounded-full flex items-center justify-center ${count===0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                              >
                                <SafeIcon icon={FiMinus} />
                              </button>
                              <span className="w-6 text-center font-medium">{count}</span>
                              <button onClick={()=> handleToppingSelection(topping.id,true)} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600">
                                <SafeIcon icon={FiPlus} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sauces */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">4</span>
                      Saucen (optional)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(productType==='burger' ? burgerOptions.sauces : pizzaOptions.sauces).map((sauce)=> {
                        const selected=orderForm.sauces[sauce.id];
                        const count=selected ? selected.count : 0;
                        const spicyLevel=orderForm.spicyLevels[sauce.id] || 1;
                        return (
                          <div key={sauce.id} className={`border rounded-lg p-3 ${count > 0 ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium flex items-center">
                                  {sauce.name}
                                  {sauce.isSpicy && <SafeIcon icon={FiFire} className="ml-1 text-orange-500" />}
                                </p>
                                <p className="text-sm text-gray-600">{sauce.price.toFixed(2)} CHF</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={()=> handleSauceSelection(sauce.id,false)}
                                  disabled={count===0}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${count===0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                                >
                                  <SafeIcon icon={FiMinus} />
                                </button>
                                <span className="w-6 text-center font-medium">{count}</span>
                                <button onClick={()=> handleSauceSelection(sauce.id,true)} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600">
                                  <SafeIcon icon={FiPlus} />
                                </button>
                              </div>
                            </div>
                            {/* Spicy level selector */}
                            {sauce.isSpicy && count > 0 && (
                              <div className="mt-3">
                                <p className="text-sm mb-1 flex items-center">
                                  <SafeIcon icon={FiFire} className="mr-1 text-orange-500" /> Schärfegrad:
                                </p>
                                <div className="flex space-x-1">
                                  {[1,2,3,4,5].map((level)=> (
                                    <button
                                      key={level}
                                      onClick={()=> handleSpicyLevelSelection(sauce.id,level)}
                                      className={`w-8 h-8 rounded-full ${spicyLevel===level ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                      {level}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sides */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">5</span>
                      Beilagen (optional)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(productType==='burger' ? burgerOptions.sides : pizzaOptions.sides).map((side)=> {
                        const selected=orderForm.sides[side.id];
                        const count=selected ? selected.count : 0;
                        return (
                          <div key={side.id} className={`border rounded-lg p-3 flex justify-between items-center ${count > 0 ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                            <div>
                              <p className="font-medium">{side.name}</p>
                              <p className="text-sm text-gray-600">{side.price.toFixed(2)} CHF</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={()=> handleSideSelection(side.id,false)}
                                disabled={count===0}
                                className={`w-8 h-8 rounded-full flex items-center justify-center ${count===0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                              >
                                <SafeIcon icon={FiMinus} />
                              </button>
                              <span className="w-6 text-center font-medium">{count}</span>
                              <button onClick={()=> handleSideSelection(side.id,true)} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600">
                                <SafeIcon icon={FiPlus} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Side Sauces - Conditionally rendered */}
                  {Object.keys(orderForm.sides).length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-xl font-bold mb-4 flex items-center">
                        <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">6</span>
                        Saucen für Beilagen (optional)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(productType==='burger' ? burgerOptions.sauces : pizzaOptions.sauces).map((sauce)=> {
                          const selected=orderForm.sideSauces[sauce.id];
                          const count=selected ? selected.count : 0;
                          return (
                            <div key={`side-${sauce.id}`} className={`border rounded-lg p-3 flex justify-between items-center ${count > 0 ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                              <div>
                                <p className="font-medium">{sauce.name}</p>
                                <p className="text-sm text-gray-600">{sauce.price.toFixed(2)} CHF</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={()=> handleSideSauceSelection(sauce.id,false)}
                                  disabled={count===0}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${count===0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                                >
                                  <SafeIcon icon={FiMinus} />
                                </button>
                                <span className="w-6 text-center font-medium">{count}</span>
                                <button onClick={()=> handleSideSauceSelection(sauce.id,true)} className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600">
                                  <SafeIcon icon={FiPlus} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Special requests */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full mr-2">
                        {Object.keys(orderForm.sides).length > 0 ? '7' : '6'}
                      </span>
                      Spezielle Wünsche (optional)
                    </h3>
                    <div className="border rounded-lg p-3">
                      <textarea
                        value={orderForm.specialRequests}
                        onChange={(e)=> handleOrderFormChange('specialRequests',e.target.value)}
                        placeholder="Haben Sie spezielle Wünsche oder Allergien? Teilen Sie uns diese hier mit..."
                        className="w-full h-24 p-2 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order summary and contact form */}
            <div>
              <div className="bg-white rounded-xl shadow-xl overflow-hidden text-gray-800 sticky top-4">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
                  <h2 className="text-2xl font-bold">Zusammenfassung</h2>
                  <p className="opacity-80">Ihre {productType==='burger' ? 'Burger' : 'Pizza'}-Bestellung</p>
                </div>
                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex justify-between items-center text-xl font-bold text-orange-500">
                      <span>Gesamtpreis:</span>
                      <span>{orderForm.totalPrice.toFixed(2)} CHF</span>
                    </div>
                  </div>

                  {/* Order type selection */}
                  {(orderConfig[productType].pickup_enabled || orderConfig[productType].delivery_enabled) && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold mb-3">Bestellart</h3>
                      <div className="flex flex-wrap gap-3">
                        {orderConfig[productType].pickup_enabled && (
                          <label className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer ${orderForm.orderType==='pickup' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                            <input
                              type="radio"
                              checked={orderForm.orderType==='pickup'}
                              onChange={()=> handleOrderFormChange('orderType','pickup')}
                              className="w-4 h-4 text-orange-500"
                            />
                            <div className="flex items-center">
                              <SafeIcon icon={FiClock} className="mr-2 text-orange-500" />
                              <span>Abholung</span>
                            </div>
                          </label>
                        )}
                        {orderConfig[productType].delivery_enabled && (
                          <label className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer ${orderForm.orderType==='delivery' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                            <input
                              type="radio"
                              checked={orderForm.orderType==='delivery'}
                              onChange={()=> handleOrderFormChange('orderType','delivery')}
                              className="w-4 h-4 text-orange-500"
                            />
                            <div className="flex items-center">
                              <SafeIcon icon={FiMapPin} className="mr-2 text-orange-500" />
                              <span>Lieferung</span>
                            </div>
                          </label>
                        )}
                      </div>
                      {/* Pickup time or delivery address */}
                      {orderForm.orderType==='pickup' && orderConfig[productType].pickup_enabled ? (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Abholzeit</label>
                          <input
                            type="time"
                            value={orderForm.pickupTime}
                            onChange={(e)=> handleOrderFormChange('pickupTime',e.target.value)}
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      ) : orderForm.orderType==='delivery' && orderConfig[productType].delivery_enabled ? (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Lieferadresse</label>
                          <textarea
                            value={orderForm.deliveryAddress}
                            onChange={(e)=> handleOrderFormChange('deliveryAddress',e.target.value)}
                            className="w-full p-2 border rounded h-20 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Straße,Hausnummer,PLZ,Ort"
                          />
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Contact information */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold mb-3">Kontaktdaten</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <div className="relative">
                          <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={orderForm.name}
                            onChange={(e)=> handleOrderFormChange('name',e.target.value)}
                            className="w-full pl-10 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Ihr Name"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                        <div className="relative">
                          <SafeIcon icon={FiPhone} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel"
                            value={orderForm.phone}
                            onChange={(e)=> handleOrderFormChange('phone',e.target.value)}
                            className="w-full pl-10 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Ihre Telefonnummer"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
                        <div className="relative">
                          <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            value={orderForm.email}
                            onChange={(e)=> handleOrderFormChange('email',e.target.value)}
                            className="w-full pl-10 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Ihre E-Mail-Adresse"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order button */}
                  <button
                    onClick={handleSubmitOrder}
                    disabled={submitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:opacity-70"
                  >
                    {submitting ? (
                      <>
                        <SafeIcon icon={FiLoader} className="animate-spin mr-2" /> Bestellung wird verarbeitet...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiShoppingCart} className="mr-2" /> Jetzt bestellen
                      </>
                    )}
                  </button>

                  {/* Error message */}
                  {orderError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start">
                      <SafeIcon icon={FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
                      <span>{orderError}</span>
                    </div>
                  )}

                  {/* Success message */}
                  {orderSuccess && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                      <div className="flex items-center mb-2">
                        <SafeIcon icon={FiCheck} className="mr-2" />
                        <span className="font-medium">Bestellung erfolgreich!</span>
                      </div>
                      <p>
                        Ihre Bestellung wurde erfolgreich aufgegeben.
                      </p>
                      <p className="text-sm mt-1">Bestellnummer: #{orderSuccess.id.substring(0,8)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer with hidden admin link */}
      <footer className="py-6 px-4 text-center text-white/80">
        <p>Wir freuen uns darauf,Ihren Traum{productType==='burger' ? 'burger' : 'pizza'} zuzubereiten.</p>
        <div className="mt-4">
          <button onClick={()=> setShowAdmin(true)} className="text-white/50 hover:text-white/80 text-sm transition-all" aria-label="Admin-Bereich">
            🍔
          </button>
          <button onClick={()=> setShowIPTester(true)} className="ml-3 text-white/50 hover:text-white/80 text-sm transition-all" aria-label="IP-Test">
            🔍
          </button>
        </div>
      </footer>
    </div>
  );
}

export default App;